import translate from '../i18n/config';

export const API_PREFIX = '/api/v1/';

export const API_URL = process.env.REACT_APP_API_URL;
export const COMPILER_URL = process.env.REACT_APP_COMPILER_URL;

const ENTITY = {
  auth: 'auth',
  mfa: 'auth/mfa',
  otp: 'auth/otp',
  users: 'users',
  me: 'users/me',
  roles: 'roles',
  genericLookup: 'generic-lookup',
  tenant: 'tenant',
  config: 'config',
  productDivisions: 'product-divisions',
  products: 'products',
  dashboard: 'dashboard',
  masters: 'masters',
  accessReviews: 'access-reviews',
  tenants: 'tenants',
  tenantUsers: 'tenant-users',
  reports: 'reports',
  auditLog: 'audit-log',
  impersonation: 'impersonation',
  subscriptions: 'subscriptions',
  payments: 'payments',
  featureFlags: 'feature-flags',
  notifications: 'notifications',
  dsar: 'dsar',
  erasureRecords: 'erasure-records',
};

export const PATH = {
  logout: 'logout',
  verifyTotp: 'verify-totp',
  sendOtp: 'send-otp',
  verifyOtp: 'verify-otp',
  requestOtp: 'request',
  verifyPasswordlessOtp: 'verify',
  search: 'search',
  lookup: 'lookup',
  validate: 'validate',
  genericLookup: 'generic-lookup',
  geoLookup: 'geo-lookup',
  template: 'template',
  upload: 'upload',
};

const API_PATHS = {
  logout: `${ENTITY.auth}/${PATH.logout}`,
  mfaVerifyTotp: `${ENTITY.mfa}/${PATH.verifyTotp}`,
  mfaSendOtp: `${ENTITY.mfa}/${PATH.sendOtp}`,
  mfaVerifyOtp: `${ENTITY.mfa}/${PATH.verifyOtp}`,
  otpRequest: `${ENTITY.otp}/${PATH.requestOtp}`,
  otpVerify: `${ENTITY.otp}/${PATH.verifyPasswordlessOtp}`,
  listRoles: `${ENTITY.roles}/${PATH.search}`,
  lookupRoles: `${ENTITY.roles}/${PATH.lookup}`,
  lookupGeneric: `${ENTITY.genericLookup}/${PATH.lookup}`,
  tenantValidate: `${ENTITY.tenant}/${PATH.validate}`,
  configLookup: `${ENTITY.config}/${PATH.genericLookup}`,
  geoLookup: `${ENTITY.config}/${PATH.geoLookup}`,
  listProductDivisions: `${ENTITY.productDivisions}`,
  productDivisions: `${ENTITY.productDivisions}`,
  lookupProductDivisions: `${ENTITY.productDivisions}/${PATH.lookup}`,
  listProducts: `${ENTITY.products}`,
  products: `${ENTITY.products}`,
  lookupProducts: `${ENTITY.products}/${PATH.lookup}`,
  suggestionsProducts: `${ENTITY.products}/suggestions`,
  tenantStatuses: `${ENTITY.masters}/tenant-statuses`,
  subscriptionStatuses: `${ENTITY.masters}/subscription-statuses`,
  paymentStatuses: `${ENTITY.masters}/payment-statuses`,
  featureFlagStatuses: `${ENTITY.masters}/feature-flag-statuses`,
  billingCycles: `${ENTITY.masters}/billing-cycles`,
  currencies: `${ENTITY.masters}/currencies`,
  masterProducts: `${ENTITY.masters}/products`,
  masterProductsLogoUpload: `${ENTITY.masters}/products/logo-upload`,
  productBranding: `${ENTITY.masters}/branding`,
  productBrandingLogoUpload: `${ENTITY.masters}/branding/logo-upload`,
  countries: `${ENTITY.masters}/countries`,
  timezones: `${ENTITY.masters}/timezones`,
  categories: `${ENTITY.masters}/categories`,
  masterRoles: `${ENTITY.masters}/roles`,
  masterRolePermissions: `${ENTITY.masters}/roles/permissions`,
  masterConfigKeys: `${ENTITY.masters}/config-keys`,
  masterNotifTemplates: `${ENTITY.masters}/notification-templates`,
  masterReceiptTemplates: `${ENTITY.masters}/receipt-templates`,
  masterPlans: `${ENTITY.masters}/plans`,
  globalConfig: `${ENTITY.config}/global`,
  internalUsers: `${ENTITY.users}/internal`,
  activate: `${ENTITY.auth}/activate`,
  accessReviews: ENTITY.accessReviews,
  tenants: ENTITY.tenants,
  tenantCheckSlug: `${ENTITY.tenants}/check-slug`,
  tenantFailedProvisioning: `${ENTITY.tenants}/provisioning/failed`,
  tenantBulkSuspend: `${ENTITY.tenants}/bulk/suspend`,
  tenantPendingPurges: `${ENTITY.tenants}/pending-purges`,
  tenantUsers: ENTITY.tenantUsers,
  tenantUserValidateToken: `${ENTITY.tenantUsers}/validate-token`,
  tenantUserActivate: `${ENTITY.tenantUsers}/activate`,
  dashboardSummary: `${ENTITY.dashboard}/summary`,
  dashboardActionItems: `${ENTITY.dashboard}/action-items`,
  reportsTenantsPreview: `${ENTITY.reports}/tenants/preview`,
  reportsTenantsExport: `${ENTITY.reports}/tenants/export`,
  reportsSubscriptionsPreview: `${ENTITY.reports}/subscriptions/preview`,
  reportsSubscriptionsExport: `${ENTITY.reports}/subscriptions/export`,
  auditLog: ENTITY.auditLog,
  auditLogExport: `${ENTITY.auditLog}/export`,
  impersonationStart: `${ENTITY.impersonation}/start`,
  impersonationEnd: `${ENTITY.impersonation}/end`,
  subscriptions: ENTITY.subscriptions,
  payments: ENTITY.payments,
  featureFlags: ENTITY.featureFlags,
  notificationsOutbox: `${ENTITY.notifications}/outbox`,
  dsar: ENTITY.dsar,
  erasureRecords: ENTITY.erasureRecords,
};

export const API_ENDPOINTS = {
  // My Account
  myProfile: `${API_PREFIX}${ENTITY.me}`,
  myAvatar: `${API_PREFIX}${ENTITY.me}/avatar`,
  myPreferences: `${API_PREFIX}${ENTITY.me}/preferences`,
  myMfaTotpInitiate: `${API_PREFIX}${ENTITY.me}/mfa/totp/initiate`,
  myMfaTotpConfirm: `${API_PREFIX}${ENTITY.me}/mfa/totp/confirm`,
  myMfaEmailOtpInitiate: `${API_PREFIX}${ENTITY.me}/mfa/email-otp/initiate`,
  myMfaEmailOtpConfirm: `${API_PREFIX}${ENTITY.me}/mfa/email-otp/confirm`,
  myMfa: `${API_PREFIX}${ENTITY.me}/mfa`,
  myMfaBackupCodes: `${API_PREFIX}${ENTITY.me}/mfa/backup-codes`,
  mySessions: `${API_PREFIX}${ENTITY.me}/sessions`,
  mySession: `${API_PREFIX}${ENTITY.me}/sessions`,
  myActivity: `${API_PREFIX}${ENTITY.me}/activity`,
  mySavedFilters: `${API_PREFIX}${ENTITY.me}/saved-filters`,
  // Auth
  logout: API_PREFIX + API_PATHS.logout,
  mfaVerifyTotp: API_PREFIX + API_PATHS.mfaVerifyTotp,
  mfaSendOtp: API_PREFIX + API_PATHS.mfaSendOtp,
  mfaVerifyOtp: API_PREFIX + API_PATHS.mfaVerifyOtp,
  otpRequest: API_PREFIX + API_PATHS.otpRequest,
  otpVerify: API_PREFIX + API_PATHS.otpVerify,
  listRoles: API_PREFIX + API_PATHS.listRoles,
  lookupRoles: API_PREFIX + API_PATHS.lookupRoles,
  configLookup: API_PREFIX + API_PATHS.configLookup,
  geoLookup: API_PREFIX + API_PATHS.geoLookup,
  tenantValidate: API_PREFIX + API_PATHS.tenantValidate,
  listProductDivisions: API_PREFIX + API_PATHS.listProductDivisions,
  addProductDivision: API_PREFIX + API_PATHS.productDivisions,
  editProductDivision: API_PREFIX + API_PATHS.productDivisions,
  getProductDivision: API_PREFIX + API_PATHS.productDivisions,
  lookupProductDivisions: API_PREFIX + API_PATHS.lookupProductDivisions,
  listProducts: API_PREFIX + API_PATHS.listProducts,
  addProduct: API_PREFIX + API_PATHS.products,
  editProduct: API_PREFIX + API_PATHS.products,
  getProduct: API_PREFIX + API_PATHS.products,
  lookupProducts: API_PREFIX + API_PATHS.lookupProducts,
  suggestionsProducts: API_PREFIX + API_PATHS.suggestionsProducts,
  tenantStatuses: API_PREFIX + API_PATHS.tenantStatuses,
  subscriptionStatuses: API_PREFIX + API_PATHS.subscriptionStatuses,
  paymentStatuses: API_PREFIX + API_PATHS.paymentStatuses,
  featureFlagStatuses: API_PREFIX + API_PATHS.featureFlagStatuses,
  billingCycles: API_PREFIX + API_PATHS.billingCycles,
  currencies: API_PREFIX + API_PATHS.currencies,
  masterProducts: API_PREFIX + API_PATHS.masterProducts,
  masterProductsLogoUpload: API_PREFIX + API_PATHS.masterProductsLogoUpload,
  productBranding: API_PREFIX + API_PATHS.productBranding,
  productBrandingLogoUpload: API_PREFIX + API_PATHS.productBrandingLogoUpload,
  countries: API_PREFIX + API_PATHS.countries,
  timezones: API_PREFIX + API_PATHS.timezones,
  categories: API_PREFIX + API_PATHS.categories,
  masterRoles: API_PREFIX + API_PATHS.masterRoles,
  masterRolePermissions: API_PREFIX + API_PATHS.masterRolePermissions,
  masterConfigKeys: API_PREFIX + API_PATHS.masterConfigKeys,
  masterNotifTemplates: API_PREFIX + API_PATHS.masterNotifTemplates,
  masterReceiptTemplates: API_PREFIX + API_PATHS.masterReceiptTemplates,
  masterPlans: API_PREFIX + API_PATHS.masterPlans,
  globalConfig: API_PREFIX + API_PATHS.globalConfig,
  internalUsers: API_PREFIX + API_PATHS.internalUsers,
  activate: API_PREFIX + API_PATHS.activate,
  accessReviews: API_PREFIX + API_PATHS.accessReviews,
  tenants: API_PREFIX + API_PATHS.tenants,
  tenantCheckSlug: API_PREFIX + API_PATHS.tenantCheckSlug,
  tenantFailedProvisioning: API_PREFIX + API_PATHS.tenantFailedProvisioning,
  tenantBulkSuspend: API_PREFIX + API_PATHS.tenantBulkSuspend,
  tenantPendingPurges: API_PREFIX + API_PATHS.tenantPendingPurges,
  tenantUsers: API_PREFIX + API_PATHS.tenantUsers,
  tenantUserValidateToken: API_PREFIX + API_PATHS.tenantUserValidateToken,
  tenantUserActivate: API_PREFIX + API_PATHS.tenantUserActivate,
  dashboardSummary: API_PREFIX + API_PATHS.dashboardSummary,
  dashboardActionItems: API_PREFIX + API_PATHS.dashboardActionItems,
  reportsTenantsPreview: API_PREFIX + API_PATHS.reportsTenantsPreview,
  reportsTenantsExport: API_PREFIX + API_PATHS.reportsTenantsExport,
  reportsSubscriptionsPreview: API_PREFIX + API_PATHS.reportsSubscriptionsPreview,
  reportsSubscriptionsExport: API_PREFIX + API_PATHS.reportsSubscriptionsExport,
  auditLog: API_PREFIX + API_PATHS.auditLog,
  auditLogExport: API_PREFIX + API_PATHS.auditLogExport,
  impersonationStart: API_PREFIX + API_PATHS.impersonationStart,
  impersonationEnd: API_PREFIX + API_PATHS.impersonationEnd,
  subscriptions: API_PREFIX + API_PATHS.subscriptions,
  payments: API_PREFIX + API_PATHS.payments,
  featureFlags: API_PREFIX + API_PATHS.featureFlags,
  notificationsOutbox: API_PREFIX + API_PATHS.notificationsOutbox,
  dsar: API_PREFIX + API_PATHS.dsar,
  erasureRecords: API_PREFIX + API_PATHS.erasureRecords,
};

export const STATUS_CODE = {
  unauthorized: 401,
  badRequest: 400,
  unProcessableEntity: 422,
  forbidden: 403,
  notFound: 404,
  internalServerError: 500,
  gatewayTimeout: 504,
  entityTooLarge: 413,
  conflict: 409,
};

export const ERROR_MESSAGES = {
  internalServerError: translate.t('common.errorMessages.internalServerError'),
  unauthorized: translate.t('common.errorMessages.unauthorized'),
  badRequest: translate.t('common.errorMessages.badRequest'),
  apiPathNotFound: translate.t('common.errorMessages.apiPathNotFound'),
  forbidden: translate.t('common.errorMessages.forbidden'),
  gatewayTimeout: translate.t('common.errorMessages.gatewayTimeout'),
};
